import {
	Card,
	Group,
	Select,
	Skeleton,
	Text,
	ThemeIcon,
	Title,
} from "@mantine/core";
import { BarChart3 } from "lucide-react";
import { useMemo, useState } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { useSnapshot } from "valtio";
import { ageYears, groupAge } from "@/api/transforms/age";
import { useIsDark } from "@/hooks/useIsDark";
import { useTranslate } from "@/hooks/useTranslate";
import { i18nStore } from "@/store/i18n";
import type { AgeData } from "./demografi.types";
import {
	AGE_AXIS_ANGLE,
	AGE_AXIS_HEIGHT,
	ROW2_CHART_HEIGHT,
} from "./demografi-layout.const";

interface DemografiAgeCardProps {
	ageData: AgeData[];
	loading: boolean;
}

export const DemografiAgeCard = ({
	ageData,
	loading,
}: DemografiAgeCardProps) => {
	const t = useTranslate();
	const dark = useIsDark();
	const { tampilkanGrid } = useSnapshot(i18nStore);

	// Dropdown tahun — default tahun terbaru, ganti tanpa refetch. Pola sama
	// dengan kartu Distribusi Agama supaya konsisten.
	const [selectedYear, setSelectedYear] = useState<string | null>(null);
	const yearOptions = useMemo(
		() => ageYears(ageData).map((y) => String(y)),
		[ageData],
	);
	const activeYear = selectedYear ?? yearOptions[0] ?? null;
	const chartData = useMemo(
		() => groupAge(ageData, activeYear ? Number(activeYear) : null),
		[ageData, activeYear],
	);

	return (
		<Card
			data-ai-target="demografi.umur"
			p="md"
			radius="xl"
			withBorder
			bg={dark ? "#1E293B" : "white"}
			style={{
				borderColor: dark ? "#334155" : "white",
				boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
			}}
			h="100%"
		>
			<Group gap="xs" mb="md" justify="space-between" wrap="nowrap">
				<Group gap="xs" wrap="nowrap">
					<ThemeIcon
						color="darmasaba-navy.7"
						variant="filled"
						size="sm"
						radius="sm"
					>
						<BarChart3 size={14} />
					</ThemeIcon>
					<Title order={4} c={dark ? "white" : "gray.9"}>
						{t.demografiPekerjaan.pengelompokanUmur}
					</Title>
				</Group>
				{yearOptions.length > 1 && (
					<Select
						size="xs"
						w={100}
						aria-label="Pilih tahun pengelompokan umur"
						data={yearOptions}
						value={activeYear}
						onChange={setSelectedYear}
						allowDeselect={false}
						comboboxProps={{ withinPortal: true }}
						wrapperProps={{ "data-ai-target": "demografi.tahun-umur" }}
					/>
				)}
			</Group>
			{loading ? (
				<Skeleton height={ROW2_CHART_HEIGHT} radius="md" />
			) : chartData.length === 0 ? (
				<Group justify="center" align="center" h={ROW2_CHART_HEIGHT}>
					<Text size="sm" c="dimmed">
						Belum ada data kelompok umur.
					</Text>
				</Group>
			) : (
				<ResponsiveContainer width="100%" height={ROW2_CHART_HEIGHT}>
					<BarChart data={chartData}>
						{tampilkanGrid && (
							<CartesianGrid
								strokeDasharray="3 3"
								vertical={false}
								stroke={dark ? "#334155" : "#e5e7eb"}
							/>
						)}
						<XAxis
							dataKey="ageRange"
							axisLine={false}
							tickLine={false}
							// Tanpa interval={0} Recharts menyembunyikan tick berselang saat
							// kategori terlalu rapat; rotasi mencegah label saling bertabrakan.
							interval={0}
							angle={AGE_AXIS_ANGLE}
							textAnchor="end"
							height={AGE_AXIS_HEIGHT}
							tick={{
								fill: dark ? "#E2E8F0" : "#374151",
								fontSize: 12,
							}}
						/>
						<YAxis
							axisLine={false}
							tickLine={false}
							tick={{
								fill: dark ? "#E2E8F0" : "#374151",
								fontSize: 12,
							}}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: dark ? "#1E293B" : "white",
								borderColor: dark ? "#334155" : "#e5e7eb",
								borderRadius: "8px",
							}}
							itemStyle={{ color: dark ? "#E2E8F0" : "#374151" }}
							labelStyle={{ color: dark ? "#E2E8F0" : "#374151" }}
						/>
						<Bar
							dataKey="total"
							fill="#396aaaff"
							radius={[8, 8, 0, 0]}
							maxBarSize={40}
						>
							<LabelList
								dataKey="total"
								position="top"
								style={{
									fill: dark ? "#E2E8F0" : "#374151",
									fontSize: 11,
								}}
							/>
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			)}
		</Card>
	);
};
